import * as PIXI from "pixi.js-legacy";

import MadmaxPixiLayer from "./MadmaxPixiLayer";
import MadmaxInfo from "./modules/MadmaxInfo";
import {inject, injectable} from "inversify";
import SERVICE_IDENTIFIER from "../../constants/identifiers";
import SystemConfig, {CONFIG_PARAMS} from "../../system/SystemConfig";
import SystemDebug from "../../system/SystemDebug";
import PixiLayers from "./PixiLayers";
import IApp from "../IApp";
import InitResult from "../../connect/result/InitResult";
import IScreenData from "../../ui/screen/IScreenData";
import IDOM from "../../ui/IDOM";
import {BASE_ELEMENT_IDS} from "../../ui/BaseDOM";

// @ts-ignore
window.PIXI = PIXI;

@injectable()
class PixiApp implements IApp {

    public layers: PixiLayers;

    protected config: SystemConfig;
    protected gameset: any;
    protected debug: SystemDebug;
    protected _app: PIXI.Application;
    protected _wintable: PIXI.Application;
    protected dom: IDOM

    constructor(
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.SystemDebug) debug: SystemDebug,
        @inject(SERVICE_IDENTIFIER.EngineAppLayers) layers: PixiLayers,
        @inject(SERVICE_IDENTIFIER.DOM) dom: IDOM
    ) {
        this.config = config;
        this.gameset = config.gameset;
        this.layers = layers;
        this.dom = dom;

        this.createApp();
    }

    createApp() {

        // //FIX IOS flickerring sprites, test if highp is supported
        // let gl = PIXI.glCore.createContext(window.document.createElement("canvas"));
        // if (gl.getShaderPrecisionFormat) {
        //     let fragmentShader = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
        //     PIXI.settings.PRECISION_FRAGMENT = fragmentShader.precision != 0 ? "highp" : "mediump";
        // }

        // PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.NEAREST;

        // PIXI.settings.RESOLUTION = window.devicePixelRatio;

        PIXI.utils.skipHello();

        let appOptions = {
            width: this.gameset.stageWidth, //568 * 2, // - (this.gameset.stageWidth * (0.5 - 0.44375)),
            height: this.gameset.stageHeight, //this.gameset.stageHeight / 2, // - (this.gameset.stageHeight * (0.5 - 0.44375)),
            // antialias: true,
            // transparent: true,
            clearBeforeRender: this.gameset.clearBeforeRender != null ? this.gameset.clearBeforeRender : false,
            //forceCanvas: true,
            // forceFXAA: true,
            // powerPreference: "high-performance",
            // resizeTo: document.getElementById("gameWrapper"),
            // resolution: window.devicePixelRatio || 1
        };

        this._app = new PIXI.Application(appOptions);
        this._app.stage.addChild(this.layers);

        // this._app.stage.scale.x = 0.75;
        // this._app.stage.scale.y = 0.75;

        // this._app.stage.interactive = true;
        // this._app.stage.on("pointerdown", (e) => {
        //     events.dispatch(ACTION_EVENTS.ACTION_STAGE_POINTER_DOWN);
        // });



        // console.log((568 * 2) / 1280, (320 * 2) / 720);
        // this._app.stage.scale.set((568 * 2) / 1280, (320 * 2) / 720);

        // window.APP = this._app;

        if(this.config.get(CONFIG_PARAMS.SEPARATED_INFOPAGES)) {
            this._wintable = new PIXI.Application(appOptions);
        }
    }

    async init() {
        this._app.view.id = BASE_ELEMENT_IDS.GAME_CANVAS;
        this.dom.getGameWrapper().appendChild(this._app.view);

        if(this.config.get(CONFIG_PARAMS.SEPARATED_INFOPAGES)) {
            this.dom.getWintableWrapper().appendChild(this._wintable.view);
        }
    }

    onResize(data: IScreenData): void {
    //onResize({canvasWidth, canvasHeight}: {canvasWidth: number, canvasHeight: number}) {

        // this.containerScale = containerScale;
        // let w = Math.round(data.canvasWidthScaled);
        // let h = Math.round(data.canvasHeightScaled);
        let w = data.canvasWidthScaled;
        let h = data.canvasHeightScaled;

        this._app.view.style.width = `${w}px`;
        this._app.view.style.height = `${h}px`;

        if(this.config.get(CONFIG_PARAMS.SEPARATED_INFOPAGES)) {
            this._wintable.view.style.width = `${w}px`;
            this._wintable.view.style.height = `${h}px`;
        }
    }

    /**
     * @return {PIXI.Container}
     */
    getStage() {
        return this._app.stage;
    }

    /**
     * @return {PIXI.Container}
     */
    getWintableStage() {
        return this._wintable && this._wintable.stage;
    }

    /**
     *
     * @return {PIXI.Renderer}
     */
    getRenderer() {
        return this._app.renderer;
    }

    /**
     *
     * @return {PIXI.Ticker}
     */
    getTicker() {
        return this._app.ticker;
    }

    loadComplete() {

        createLayers(this.layers, this.gameset.game.layers, this.gameset);
        createGrelems(this.layers, this.gameset.game.grelems);

        // this._layers.scale.x = 568 / 960 * this.containerScale;
        // this._layers.scale.y = 320 / 540 * this.containerScale;

        // this._layers.addLayerToTop("loader");
    }

    onInitSuccess(initResult: InitResult): void {}
}

export default PixiApp;

function validateLayerName(name) {
    return !(name.indexOf(".") != -1);
}

function createLayers(layers, layersList, gameset) {

    layersList && layersList.forEach(layer => {

        let name = typeof layer === "string" ? layer : layer.name;

        if(!validateLayerName(name)) {
            throw Error("Layer name " + name + " cannot contain dot!");
        }

        let newLayer;

        switch (name) {
            // case "dynamicText":
            //     newLayer = layers.addLayer(name, new DynamicText());
            //     break;
            case "info":
                newLayer = layers.addLayer(name, new MadmaxInfo(gameset));
                break;
            default:
                newLayer = layers.addLayer(name, new MadmaxPixiLayer());
                break;
        }

        if(typeof layer === "object") {
            createLayers(newLayer, layer.layers, gameset);
        }

    });
}

/**
 * @param {PixiLayers} layers
 * @param {Array<Object>} grelems
 */
function createGrelems(layers, grelems) {
    grelems && grelems.forEach(grelem => {

        let sprite = new PIXI.Sprite(PIXI.Texture.from(grelem.frame + grelem.sufix));
        if(grelem.set) {
            if(grelem.set.x) sprite.x = grelem.set.x;
            if(grelem.set.y) sprite.y = grelem.set.y;
            if(grelem.set.width) sprite.width = grelem.set.width;
            if(grelem.set.height) sprite.height = grelem.set.height;
        }
        if(grelem.scale) {
            if(grelem.scale.x) sprite.scale.x = grelem.scale.x;
            if(grelem.scale.y) sprite.scale.y = grelem.scale.y;
        }
        if(grelem.anchor) {
            if(grelem.anchor.x) sprite.anchor.x = grelem.anchor.x;
            if(grelem.anchor.y) sprite.anchor.y = grelem.anchor.y;
        }
        if(typeof grelem.alpha != "undefined") {
            sprite.alpha = grelem.alpha;
        }
        if(typeof grelem.visible != "undefined") {
            sprite.visible = grelem.visible;
        }

        sprite.name = grelem.name;

        let layer = layers.getLayer(grelem.layer);
        if(layer) {
            layer.addGrelem(sprite.name, sprite);
        }
    });
}
